package com.bikompanion.data.analytics

import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import com.bikompanion.data.analytics.web.Ga4EventQueue
import com.bikompanion.data.analytics.web.WebAnalyticsIdentity
import com.bikompanion.domain.analytics.AnalyticsEvent
import kotlin.js.Date
import kotlin.random.Random

/** Long enough to collect the burst a screen change produces, short enough to survive a quick exit. */
private const val FlushDelayMillis = 1_000
private const val MaxClientNumber = 10_000_000_000L

/**
 * Reports through the app's own `/api/analytics` endpoint, which forwards to the GA4
 * Measurement Protocol with the API secret added at the edge.
 *
 * Same-origin rather than the Firebase JS SDK, as in Secret Chords: the secret never reaches the
 * bundle, the request isn't a third-party call for blockers to drop, and the edge can state the
 * visitor's coarse location without sending their IP address on.
 */
private var queue: Ga4EventQueue? = null

internal actual fun configurePlatformTelemetry(enabled: Boolean) {
    if (!enabled || queue != null) return
    val created = Ga4EventQueue(
        identity = WebAnalyticsIdentity(
            visitorStorage = BrowserAnalyticsStorage { localStorage },
            tabStorage = BrowserAnalyticsStorage { sessionStorage },
            randomNumber = { Random.nextLong(MaxClientNumber) },
        ),
        defaultParameters = mapOf(PlatformParameter to "web"),
        userProperties = mapOf(PlatformParameter to "web"),
        pageLocation = window.location.origin + "/",
        nowMillis = { Date.now().toLong() },
        scheduleFlush = { flush -> window.setTimeout({ flush() }, FlushDelayMillis) },
        send = ::sendAnalytics,
    )
    // The last chance to send what is queued. `visibilitychange` is the one that fires on a mobile
    // tab switch, where `pagehide` and `unload` may never run.
    document.addEventListener("visibilitychange", {
        if (document.asDynamic().visibilityState == "hidden") created.flush()
    })
    window.addEventListener("pagehide", { created.flush() })
    queue = created
}

internal actual fun trackPlatformEvent(event: AnalyticsEvent) {
    queue?.track(event)
}

/**
 * `sendBeacon` is the only transport the browser promises to finish after the page goes away,
 * which is when the last batch is sent. A keepalive fetch covers browsers that refuse it.
 */
@Suppress("UNUSED_PARAMETER")
private fun sendAnalytics(payload: String) {
    js(
        """
        (function (body) {
            var endpoint = '/api/analytics';
            try {
                if (navigator.sendBeacon && navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }))) return;
            } catch (ignored) {}
            try {
                fetch(endpoint, {
                    method: 'POST',
                    keepalive: true,
                    headers: { 'Content-Type': 'application/json' },
                    body: body
                }).catch(function () {});
            } catch (ignored) {}
        })(payload)
        """,
    )
}
